import { useCallback, useEffect, useMemo, useState } from 'react';
import base64url from 'base64url';
import { TimesheetLabelValue } from 'graphql/types';
import { useBillingLabelSearch } from 'modules/timesheet-labels/hooks/useBillingLabelSearch';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { DependantLabels } from '../schema';

type UseActivityChipData = {
  options: TimesheetLabelValue[];
  handleSearch: (queryText: string) => void;
  loading: boolean;
  hasMore: boolean;
  label?: TimesheetLabel | null;
};

export const isThereMore = (
  cursor: number | undefined,
  totalCount: number | undefined
): boolean => {
  return cursor && totalCount ? cursor < totalCount : false;
};

export const useBilling = (
  dependentLabels: DependantLabels,
  billingChipOpen: boolean
): UseActivityChipData => {
  const [options, setOptions] = useState<TimesheetLabelValue[]>([]);
  const [lastSearch, setLastSearch] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const { searchHandler, data, loading } = useBillingLabelSearch();

  const { project, task } = dependentLabels;
  const projectUri = project?.label?.value?.idValue || '';
  const taskUri = task?.label?.value?.idValue || undefined;

  useEffect(() => {
    setLastSearch(undefined);
  }, [billingChipOpen]);

  const handleSearch = useCallback(
    (queryText: string) => {
      if (lastSearch !== queryText) {
        searchHandler({
          query: queryText || undefined,
          projectUri: projectUri,
          taskUri: taskUri
        });
      }
      setLastSearch(queryText);
    },
    [lastSearch, projectUri, searchHandler, taskUri]
  );

  useEffect(() => {
    if (loading) {
      setOptions([]);
      setHasMore(false);
    } else if (data) {
      const billingRates = data?.timesheetLabelValueSearch?.billingRates?.items;
      const cursor = data?.timesheetLabelValueSearch?.billingRates?.cursor
        ? parseInt(
            base64url.decode(
              data?.timesheetLabelValueSearch?.billingRates?.cursor
            )
          )
        : undefined;
      const totalCount =
        data?.timesheetLabelValueSearch?.billingRates?.totalCount;
      setOptions(billingRates || []);
      setHasMore(isThereMore(cursor, totalCount));
    }
  }, [data, loading]);

  return useMemo(
    () => ({
      options,
      handleSearch,
      loading,
      hasMore,
      label: dependentLabels.billing?.label
    }),
    [options, handleSearch, loading, hasMore, dependentLabels.billing?.label]
  );
};

export default useBilling;
